import { ApiError, apiGet } from './client';

async function send<T = unknown>(
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data: T | { error?: string };
  try {
    data = await res.json();
  } catch {
    data = {} as T;
  }
  if (!res.ok) {
    const detail = (data as { error?: string }).error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(detail, res.status);
  }
  return data as T;
}

export type MiabWindowState = 'upcoming' | 'open' | 'closed';
export type MiabQType = 'single' | 'multi' | 'rating' | 'text';

export type MiabSurvey = {
  id: number;
  slug: string;
  title: string;
  box_label: string;
  intro: string | null;
  opens_at: string;
  closes_at: string;
  order_window_start: string | null;
  order_window_end: string | null;
  is_published: 0 | 1;
  state: MiabWindowState;
  question_count?: number;
  response_count?: number;
};

export type MiabOption = {
  id: number;
  question_id: number;
  label: string;
  image_url: string | null;
  product_url: string | null;
  vendor: string | null;
  sort_order: number;
};

export type MiabQuestion = {
  id: number;
  survey_id: number;
  prompt: string;
  help_text: string | null;
  qtype: MiabQType;
  required: 0 | 1;
  sort_order: number;
  max_length: number | null;
  rating_min: number | null;
  rating_max: number | null;
  rating_min_label: string | null;
  rating_max_label: string | null;
  options: MiabOption[];
};

export type MiabExistingAnswer = {
  questionId: number;
  optionId: number | null;
  rating: number | null;
  text: string | null;
};

export type MiabSurveyData = {
  survey: MiabSurvey;
  questions: MiabQuestion[];
  responses: Array<{ af_number: string; answers: MiabExistingAnswer[] }>;
  canRespond: boolean;
};

// One answer per question, shaped by question type.
export type MiabAnswerInput = {
  questionId: number;
  optionIds?: number[]; // single / multi
  ratings?: Record<string, number>; // rating: optionId -> value
  text?: string; // text
};

export type MiabResultOption = {
  id: number;
  label: string;
  image_url: string | null;
  picks: number;
  avgRating: number | null;
  ratingCount: number;
};

export type MiabResultQuestion = {
  id: number;
  prompt: string;
  qtype: MiabQType;
  rating_max: number | null;
  options: MiabResultOption[];
  textAnswers?: Array<{ af: string; text: string }>;
};

export type MiabResults = {
  survey: MiabSurvey;
  responses: number;
  results: MiabResultQuestion[];
};

export type MiabSurveyInput = {
  slug: string;
  title: string;
  box_label: string;
  intro: string | null;
  opens_at: string;
  closes_at: string;
  order_window_start: string | null;
  order_window_end: string | null;
  is_published: boolean;
};

export type MiabQuestionInput = {
  prompt: string;
  help_text: string | null;
  qtype: MiabQType;
  required: boolean;
  max_length?: number | null;
  rating_min?: number | null;
  rating_max?: number | null;
  rating_min_label?: string | null;
  rating_max_label?: string | null;
  options: Array<{
    label: string;
    image_url?: string | null;
    product_url?: string | null;
    vendor?: string | null;
  }>;
};

export const miabApi = {
  /** Returns null when no survey is currently open (server sends 204). */
  async active(): Promise<MiabSurveyData | null> {
    const res = await fetch('/api/miab/active', { credentials: 'include' });
    if (res.status === 204) return null;
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) detail = body.error;
      } catch {}
      throw new ApiError(detail, res.status);
    }
    return res.json() as Promise<MiabSurveyData>;
  },

  bySlug: (slug: string) => apiGet<MiabSurveyData>(`/api/miab/survey/${slug}`),

  respond: (slug: string, af: string, answers: MiabAnswerInput[]) =>
    send<{ ok: true; responseId: number }>(`/api/miab/${slug}/respond`, 'POST', { af, answers }),

  adminSurveys: () => apiGet<{ surveys: MiabSurvey[] }>('/api/miab/admin/surveys'),

  results: (id: number) => apiGet<MiabResults>(`/api/miab/admin/surveys/${id}/results`),

  createSurvey: (body: MiabSurveyInput) =>
    send<{ id: number }>('/api/miab/admin/surveys', 'POST', body),

  updateSurvey: (id: number, body: MiabSurveyInput) =>
    send<{ ok: true }>(`/api/miab/admin/surveys/${id}`, 'PUT', body),

  replaceQuestions: (id: number, questions: MiabQuestionInput[]) =>
    send<{ ok: true; count: number }>(`/api/miab/admin/surveys/${id}/questions`, 'PUT', { questions }),

  /**
   * Uploads one item photo and returns the URL to store on the option. Sent as
   * multipart, so no Content-Type header — the browser sets it with the
   * boundary, and setting it by hand makes the body unparseable.
   */
  async uploadImage(file: File): Promise<string> {
    const body = new FormData();
    body.append('image', file);
    const res = await fetch('/api/miab/admin/images', {
      method: 'POST',
      credentials: 'include',
      body,
    });
    let data: { url?: string; error?: string } = {};
    try {
      data = await res.json();
    } catch {}
    if (!res.ok || !data.url) {
      throw new ApiError(data.error ?? `${res.status} ${res.statusText}`, res.status);
    }
    return data.url;
  },
};
